import { useMemo } from 'react';
import { CalendarType } from '@demind-inc/core';

import { useAuthContext, useCalendarContext, useDeleteCalendarsInfo } from '../data-access';
import { useConnectCalendar } from './useConnectCalendar';

interface UseHandleCalSyncErrorProps {
  syncErrorRootEmail?: string;
}

export const useHandleCalSyncError = ({ syncErrorRootEmail }: UseHandleCalSyncErrorProps) => {
  const { calendarsMeta } = useCalendarContext();
  const { user } = useAuthContext();
  const { deleteCalendarsInfo, isPending: isDisconnectingCalendars } = useDeleteCalendarsInfo();
  const { connectCal, isLoading: isConnectingCalendars } = useConnectCalendar();

  const syncErrorCalendars = useMemo(
    () =>
      calendarsMeta.filter(
        ({ rootEmail, calendarType }) =>
          rootEmail === syncErrorRootEmail && calendarType !== 'lifestack'
      ),
    [calendarsMeta, syncErrorRootEmail]
  );

  const syncErrorInfo = useMemo(() => {
    if (!syncErrorCalendars.length) {
      return undefined;
    }

    const calendarIds = syncErrorCalendars.map(({ calendarId }) => calendarId);
    const calendarType = syncErrorCalendars[0]?.calendarType as CalendarType | undefined;

    return {
      calendarIds,
      calendarType,
    };
  }, [syncErrorCalendars]);

  const handleConnect = async (calendarType: CalendarType): Promise<boolean> => {
    return connectCal(calendarType);
  };

  const handleDisconnect = async (calendarIds: string[]) => {
    await deleteCalendarsInfo({
      userId: user.userId,
      targetCalendarIds: calendarIds,
    });
  };

  return {
    syncErrorCalendars,
    syncErrorInfo,
    handleConnect,
    handleDisconnect,
    isConnectingCalendars,
    isDisconnectingCalendars,
  };
};
